/* 
©2018 EdgeVerve Systems Limited (a fully owned Infosys subsidiary),
Bangalore, India. All Rights Reserved.
 */

import { Polymer, html } from '@polymer/polymer/polymer-legacy.js';

/**
 * This polymer element converts transaction data into a PDF to share via socialShare options.
 * The element acts as a single end-point to generate PDF from transaction data (e.g. transaction receipt, history, etc). We can use this component to convert the data to PDF in our page, we should pass the required transaction information in JSON object format to this wrapper for the same. Then it is the responsibility of the wrapper to take care of data binding, UI element generation.In device you will get "Share" icon for Sharing converted PDF via socialShare options where as in browser "Download" icon is available for Downloading PDF.
 * @customElement
 * @polymer
 * @demo node_modules/speedboat_ui/elements/demo/app-fab-demo.html
 * @summary Technical component
*/
const template = html`
        <style include="app-styles">
             :host {
                @apply --layout-vertical;
                @apply --layout-center-center;
                border-radius: 50%;
                padding: 0;
                color: var(--primary-light-color);
                text-align: center;
            }

            :host.light-background {
                background: var(--primary-light-color);
            }

             :host([extra-extra-small]), :host([extra-extra-small]) .avatar-style-text {
                width: 28px;
                height: 28px;
                min-width: 28px;
                min-height: 28px;
            }
            
             :host([extra-extra-extra-small]), :host([extra-extra-extra-small]) .avatar-style-text {
                width: 24px;
                height: 24px;
                min-width: 24px;
                min-height: 24px;
            }

             :host([extra-small]), :host([extra-small]) .avatar-style-text  {
                width: 32px;
                height: 32px;
                min-width: 32px;
                min-height: 32px;
            }

             :host([small]), :host([small]) .avatar-style-text  {
                width: 48px;
                height: 48px;
                min-width: 48px;
                min-height: 48px;
            }

             :host([medium]), :host([medium]) .avatar-style-text  {
                width: 60px;
                height: 60px;
                min-width: 60px;
                min-height: 60px;
            }

             :host([large]), :host([large]) .avatar-style-text  {
                width: 80px;
                height: 80px;
                min-width: 80px;
                min-height: 80px;
            }

             :host([xlarge]), :host([xlarge]) .avatar-style-text  {
                width: 112px;
                height: 112px;
                min-width: 112px;
                min-height: 112px;
            }

            .chartColor1 {
                background: var(--chart-circle-color-light-1);
            }

            .chartColor2 {
                background: var( --chart-circle-color-light-2);
            }

            .chartColor3 {
                background: var(--chart-circle-color-light-3);
            }

            .chartColor4 {
                background: var( --chart-circle-color-light-4);
            }

            .chartColor5 {
                background: var(--chart-circle-color-light-5);
            }

            .chartColor6 {
                background: var(--chart-circle-color-light-6);
            }

            .chartColor7 {
                background: var(--chart-circle-color-light-7);
            }

            .avatar-style {
                font-family: var(--light-font);
                font-size: large;
                width: 100%;
                min-height: -webkit-fill-available;
                display: flex;
                border-radius: 50%;
                position: relative;
                @apply --avatar-style;
            }

            .avatar-style-text {
                display: flex;
                vertical-align: middle;
                border-radius: 50%;
                text-align: center;
            }

            .icon-style {
                border-radius: 50%;
                min-height: 100%;
                width: 100%;
                @apply --app-fab-icon-style;
            }
            
            .action-icon-indicator {
                width: 15px;
                height: 15px;
                top: -5px;
                right: -3px;
                @apply --action-icon-style;

            }
			
			:dir(rtl) .action-icon-indicator {
                width: 15px;
                height: 15px;
                top: -5px;
                left: -3px;
				right: auto;
                @apply --action-icon-style;

            }
            
            .action-icon-pending {
                --iron-icon-fill-color: var(--chart-circle-color-light-5);
            }

            @-moz-document url-prefix() {
                .avatar-style, .avatar-wrapper {
                    height: 100%;
                }
            } 
            
        </style>
        
        
        <div class="avatar-wrapper position-relative full-width">
            <template is="dom-if" if="{{_iconPresent(image,icon)}}">
                <iron-icon id="appFabIcon" class$="icon-style {{fabTextColor}}" id="icon-image" icon="{{icon}}"></iron-icon>
            </template>
            <template is="dom-if" if="{{_imagePresent(image,icon)}}">
                <img alt="Not found" id="appFabImage" alt="logo" class="avatar-style" src="{{image}}">
            </template>
            <div class="avatar-style" role$="{{appFabNameRole}}" aria-label$="{{getLiteral(appFabNameLabel)}}" hidden$="{{_imageOrIconPresent(image, icon)}}">            
                <div id="appFabName" class$="{{fabBackgroundColor}} avatar-style-text content-align-center">
                    <template is="dom-if" if="{{!showFullString}}">
                        <span id="appFabFirstLetter" class$="{{fabTextColor}}" aria-hidden="true">{{_getFirstLetter(name)}}</span>
                    </template>
                    <template is="dom-if" if="{{showFullString}}">
                        {{_getFullString(name)}}
                    </template>
                </div>
            </div>
            <iron-icon tabindex=0 role="button" on-keydown="keyEnterAction" class="outlineOffset position-absolute-t-r success-icon-m action-icon-indicator light-background border-round-24px " data-item={{index}} icon="{{actionIcon}}" hidden$="{{actionIconIndicator}}" on-tap="clickAction"></iron-icon>
        </div>`;

Polymer({
    is: "app-fab",
    _template: template,
    properties: {
        /* property having the value of image path */
        imageServer: {
            type: String,
            value: window.OEUtils.baseurl
        },
        /* property to show successtick at the end */
        actionIcon: {
            type: String,
            value: "successtick"
        },
        /* property having the image value  */
        image: {
            type: String,
            notify: true,
            value: ''
        },
        /* property for background Color  */
        fabBackgroundColor: {
            type: String,
            notify: true,
            value: ''
        },
        /* property for icon  */
        icon: {
            type: String,
            notify: true,
            value: ''
        },
        /* Property to show/hide full Name */
        showFullString: {
            type: Boolean,
            value: false,
            notify: true
        },
        /* Property to show/hide action Icon */
        actionIconIndicator: {
            type: Boolean,
            value: true,
            notify: true
        },
        /* Property to show/hide action Icon */
        assignFabBackground: {
            type: String,
            value: "",
            notify: true
        },
        /* Property for icon text color */
        fabTextColor: {
            type: String,
            value: "",
            notify: true
        },
        /* Property for fab name role */
        appFabNameRole: {
            type: String,
            notify: true
        },
        /* Property for fab name label */
        appFabNameLabel: {
            type: String,
            notify: true
        }

    },
    /** 
     * Method to execute when an action icon is clicked
     * @event
    **/
    clickAction: function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.fire('app-fab-action');
    },
    /** 
     * Method to show full name
     * @param {string} name
     * @return {string}
    **/
    _getFullString: function (name) {
        if (name) {
            this._pickBackgroundColor(name)
            return name;
        }
    },
    /** 
     * Method to show first Letter
     * @param {string} name
     * @return {string}
    **/
    _getFirstLetter: function (name) {
        if (name) {
            this._pickBackgroundColor(name)
            return name.charAt(0).toUpperCase();
        }
    },
    /** 
     * Method to trigger when click action is selected through keyboard.
     * @event
    **/
    keyEnterAction: function (keyboardEvent) {
        // enter pressed!
        if (keyboardEvent.keyCode === 13) {
            this.clickAction(keyboardEvent);
        }
    },
    /** 
     * Method to check whether icon or image is present.
     * @param {string} image
     * @param {string} icon
     * @return {boolean}
    **/
    _imageOrIconPresent: function (image, icon) {
        if ((typeof image === "string" && image.length > 0) || (typeof icon === "string" && icon.length > 0))
            return true;
        return false;
    },
    /** 
     * Method to check whether icon is present.
     * @param {string} image
     * @param {string} icon
     * @return {boolean}
    **/
    _iconPresent: function (image, icon) {
        if ((typeof icon === "string" && icon.length > 0) && (typeof image === "string" && image.length == 0))
            return true;
        return false;
    },
    /** 
     * Method to check whether image is present.
     * @param {string} image
     * @param {string} icon
     * @return {boolean}
    **/
    _imagePresent: function (image, icon) {
        if ((typeof image === "string" && image.length > 0) && (typeof icon === "string" && icon.length == 0))
            return true;
        return false;
    },
    /** 
     * Method to select background color
     * @param {string} name
    **/
    _pickBackgroundColor: function (name) {
        //Checks if the host has any overriding class.
        //this classList.node.classList is not working in Polymer3, to be checked.
        //var clsList = Polymer.dom(this).classList.node.classList;
        //if (clsList.length > 0 && (clsList[0] === "style-scope" || clsList[0] === "x-scope") && !_.isNil(name)) {
        if (!_.isNil(name) && this.assignFabBackground === '') {
            name = name.toString().toLowerCase();
            if (!this.allColors)
                this.allColors = ['chartColor1', 'chartColor2', 'chartColor3', 'chartColor4', 'chartColor5', 'chartColor6', 'chartColor7'];
            if (!window.colorMap)
                window.colorMap = new Map();
            var nameInMap = window.colorMap.get(name) ? window.colorMap.get(name).color : undefined;
            var nextColor = undefined;
            if (nameInMap)
                this.fabBackgroundColor = nameInMap;
            else {
                //get next color from the all colors list
                nextColor = this._getNextColor(this.allColors);
                window.colorMap.set(name, { color: nextColor });
                if (nextColor) {
                    this.fabBackgroundColor = nextColor;
                }
                else {
                    this.fabBackgroundColor = '';
                }
                // return nextColor ? nextColor : "";
            }
        }
        else {
            this.fabBackgroundColor = this.assignFabBackground;
        }

    },
    /**
     * Method to select background color if previous color in aray is selected
     * @param {array} allColors
     * @return {string}
    **/
    _getNextColor: function (allColors) {
        if (!_.isEmpty(allColors)) {
            var mapSize = window.colorMap.size;
            var allColorsSize = allColors.length;
            var nextColorIndex = mapSize % allColorsSize;
            return allColors[nextColorIndex];
        }
        return undefined;
    },
    /** 
     * Method to get the literal value
     * @param {string} literal
     * @return {string}
    **/
    getLiteral: function (literal) {
        return getLabel(literal);
    }

});
